"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react"

type Variant = "default" | "destructive"

interface Toast {
  id: number
  title: string
  description?: string
  variant?: Variant
}

interface ToastContextType {
  toast: (opts: Omit<Toast, "id">) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

/**
 * An auto-dismissing toast is a time limit on reading content, which WCAG 2.2.1
 * (Timing Adjustable, Level A) requires the user to be able to turn off, adjust
 * or extend. Three mechanisms cover it: a dismiss button (turn off),
 * pause-on-hover and pause-on-focus (extend), and a longer clock for the
 * destructive variant, which carries errors and so more reading.
 */
const DEFAULT_DURATION_MS = 3000
const DESTRUCTIVE_DURATION_MS = 8000

interface Timer {
  timeout: ReturnType<typeof setTimeout>
  remaining: number
  startedAt: number
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  // Timers live in a ref rather than state: pausing must not re-render, because
  // re-rendering a live region risks re-announcing its contents.
  const timers = useRef(new Map<number, Timer>())
  // Date.now() collides when two toasts fire within the same millisecond, which
  // gives React duplicate keys and — now that ids drive dismissal — would make
  // closing one toast close its twin.
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer.timeout)
    timers.current.delete(id)
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const start = useCallback(
    (id: number, ms: number) => {
      timers.current.set(id, {
        timeout: setTimeout(() => dismiss(id), ms),
        remaining: ms,
        startedAt: Date.now(),
      })
    },
    [dismiss]
  )

  const pause = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (!timer) return
    clearTimeout(timer.timeout)
    timer.remaining -= Date.now() - timer.startedAt
  }, [])

  const resume = useCallback(
    (id: number) => {
      const timer = timers.current.get(id)
      if (!timer) return
      start(id, Math.max(timer.remaining, 0))
    },
    [start]
  )

  // Deliberately has no de-duplication: callers such as
  // lib/hooks/use-user-error-toast.ts own the decision about when to announce
  // (once per entry into an error state, re-arming on recovery). Suppressing a
  // repeat here would make a second failure silent after a manual dismiss.
  const toast = useCallback(
    (opts: Omit<Toast, "id">) => {
      const id = nextId.current++
      const variant = opts.variant ?? "default"
      // `variant` is written after the spread on purpose: `{...opts}` last put
      // an explicit `undefined` over the default whenever a caller omitted it.
      setToasts((prev) => [...prev, { ...opts, id, variant }])
      start(id, variant === "destructive" ? DESTRUCTIVE_DURATION_MS : DEFAULT_DURATION_MS)
    },
    [start]
  )

  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach((timer) => clearTimeout(timer.timeout))
      pending.clear()
    }
  }, [])

  // `toast` is referentially stable — every dependency is a ref or a []-dep
  // callback — so consumers may list it in effect deps without re-firing.
  const value = useMemo(() => ({ toast }), [toast])

  const cardProps = { onDismiss: dismiss, onPause: pause, onResume: resume }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Two viewports, both permanently mounted. A live region only announces
          content inserted into a region the AT was already observing, so the
          container has to exist before the toast does — mounting the region and
          the toast together announces nothing. Polite and assertive are split
          because the politeness setting lives on the region, not the message:
          a destructive toast interrupts, a default one waits its turn. */}
      <div className="fixed top-4 right-4 z-50 flex flex-col items-end gap-2">
        <div role="alert" aria-live="assertive" aria-atomic="false" className="space-y-2">
          {toasts
            .filter((t) => t.variant === "destructive")
            .map((t) => (
              <ToastCard key={t.id} toast={t} className="bg-red-600" {...cardProps} />
            ))}
        </div>
        <div role="status" aria-live="polite" aria-atomic="false" className="space-y-2">
          {toasts
            .filter((t) => t.variant !== "destructive")
            .map((t) => (
              <ToastCard key={t.id} toast={t} className="bg-neutral-800" {...cardProps} />
            ))}
        </div>
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({
  toast: t,
  className,
  onDismiss,
  onPause,
  onResume,
}: {
  toast: Toast
  className: string
  onDismiss: (id: number) => void
  onPause: (id: number) => void
  onResume: (id: number) => void
}) {
  return (
    <div
      onMouseEnter={() => onPause(t.id)}
      onMouseLeave={() => onResume(t.id)}
      // React maps onFocus/onBlur to focusin/focusout, which bubble, so tabbing
      // to the dismiss button inside pauses its parent's clock.
      onFocus={() => onPause(t.id)}
      onBlur={() => onResume(t.id)}
      className={`flex items-start gap-3 rounded-md px-4 py-2 shadow-lg text-white ${className}`}
    >
      <div className="min-w-0">
        <strong>{t.title}</strong>
        {t.description && <div className="text-sm">{t.description}</div>}
      </div>
      {/* No opacity on this control in any state. Element opacity composites the
          glyph and its backdrop against the same surface, scaling both, so the
          ratio collapses however it is tuned — hover is a background change. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(t.id)}
        className="-mr-2 shrink-0 rounded p-1 text-lg leading-none text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used within ToastProvider")
  return ctx
}
